import docIcon from '@/assets/images/upload_doc.png';
import uploadIcon from '@/assets/images/upload_icon.png';
import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';
import { Box, Button, Grid, Typography } from '@mui/material';
import baseAxios from 'axios';
import React, { useState } from 'react';
import axios from '../../../_utils/axios';
import { selectCurrentPathString, selectHomePathId } from '../../../redux/selectors/fileManagerSelectors';
import { refreshFiles } from '../../../redux/slices/fileManagerSlice';
import { useToast } from '../../_common/Toast';
import DroppedFile from './DroppedFile';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

const DragDropFiles = ({ setUploading, uploading, closeModal }) => {
  const dispatch = useAppDispatch();
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const inputRef = React.useRef(null);
  const currentPath = useAppSelector(selectCurrentPathString);
  const currentPathId = useAppSelector(selectHomePathId);
  const { error, success } = useToast();

  const uploadFiles = () => {
    if (!currentPathId) return error('There was an issue selecting the sourch path.');

    setUploading(true);
    [...files].forEach((file) =>
      axios
        .post(`/vfs/file_sources/${currentPathId}/upload/create/`, {
          filename: `${currentPath}${currentPath.length ? '/' : ''}${file.name}`
        })
        .then((res) => {
          let data = new FormData();
          const signedUrl = res.data?.data?.config?.url;
          const fields = res.data?.data?.config?.fields;
          Object.keys(fields).forEach((key) => data.append(key, fields[key]));
          data.append('file', file, file.name);
          baseAxios.post(signedUrl, data, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        })
        .then(() => {
          inputRef.current.value = null;
          success('File(s) uploaded successfully!');
          setUploading(false);
          setFiles([]);
          dispatch(refreshFiles());
          closeModal();
        })
        .catch((err) => {
          error(err.response.message || 'There was an error uploading the file(s).');
          setUploading(false);
        })
    );
  };

  const handleFiles = (droppedFiles) => {
    // remove any duplicate files from droppedFiles that are in files
    const filteredFiles = [...droppedFiles].filter((file) => !files.some((f) => f.name === file.name));
    setFiles([...files, ...filteredFiles]);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const removeFileFromList = (file) => {
    setFiles(files.filter((f) => f.name !== file.name));
  };

  // handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else {
      setDragActive(false);
    }
  };

  // triggers when file is dropped
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // triggers when file is selected with click
  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files?.[0]) {
      handleFiles(e.target.files);
    }
  };

  // triggers the input when the button is clicked
  const onButtonClick = () => {
    inputRef.current.click();
  };

  return (
    <>
      <DragAndDropBox data-dragactive={dragActive}>
        <form
          id="form-file-upload"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onSubmit={(e) => e.preventDefault()}
        >
          <input ref={inputRef} type="file" id="input-file-upload" multiple onChange={handleChange} style={{ display: 'none' }} />
          <label id="label-file-upload" htmlFor="input-file-upload">
            <DragAndDropIcons>
              <IconWrapper>
                <UploadIcon src={uploadIcon} alt="upload icon" height={50} />
                <DocIcon src={docIcon} alt="upload icon" height={65} />
                <DocIcon src={docIcon} alt="upload icon left" height={65} />
                <DocIcon src={docIcon} alt="upload icon right" height={65} />
              </IconWrapper>
              <Grid container justifyContent="center" alignItems="center">
                <Typography variant="caption">Drag to upload or</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  <BrowseButton variant="text" onClick={onButtonClick}>
                    Browse Files
                  </BrowseButton>
                </Typography>
              </Grid>
            </DragAndDropIcons>
          </label>
          {dragActive && <div id="drag-file-element" onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} />}
        </form>
      </DragAndDropBox>
      <Box sx={{ maxHeight: '60vh', overflow: 'auto', width: '100%' }}>
        {files.map((file, index) => (
          <DroppedFile key={`${file.lastModified}_${file.name}`} index={index} file={file} onRemove={removeFileFromList} />
        ))}
      </Box>
      <ActionButtons>
        <Button variant="contained" onClick={uploadFiles} endIcon={<PolarisIcon icon="right" height={14} />} disabled={!files.length || !!uploading}>
          Upload {files.length > 0 && <strong>{files.length}</strong>} files
        </Button>
      </ActionButtons>
    </>
  );
};

export default DragDropFiles;

const ActionButtons = styled(Box)`
  ${({ theme }) => `
    margin-top: ${theme.spacing(2)};
    display: flex;
    justify-content: flex-end;
    width: 100%;
    .MuiButton-root strong {
      margin: ${theme.spacing(0.5)};
    }
  `}
`;

const DragAndDropBox = styled(Box)`
  ${({ theme, ...props }) => {
    const isDragging = props['data-dragactive'];
    return `
      width: 100%;
      border: 2.5px dashed ${theme.palette.grey[600]};
      border-radius: 20px;
      text-align: center;
      margin: ${theme.spacing(4, 0, 3)};
      transition: border-color 0.3s ease, background-color 0.3s ease;
      border-color: ${isDragging ? theme.palette.primary.main : theme.palette.grey[600]};
      background-color: ${isDragging ? theme.palette.primary.main + '1a' : 'transparent'};

      img:first-of-type {
        transition: filter 0.3s ease;
        filter: ${!isDragging ? 'grayscale(1) invert(1) contrast(1.5) brightness(.5)' : 'none'};
      }

      img:nth-of-type(2) {
        opacity: ${isDragging ? 0.4 : 0};
        transition: transform 0.1s ease-out, opacity 0.1s ease;
        transform: ${isDragging ? 'translateX(-118%) translateY(35%) rotate(-30deg) scale(.9)' : 'translateX(0) translateY(0) rotate(deg) scale(1)'};
        transform-origin: top center;
      }

      img:nth-of-type(3) {
        opacity: .4;
        transition: transform 0.1s ease-out, opacity 0.1s ease;
        transform: ${isDragging ? 'translateX(100%) translateY(35%) rotate(30deg) scale(.9)' : 'translateX(0) translateY(0) rotate(deg) scale(1)'}; 
        transform-origin: top center;
      }

      form {
        padding: 20px;
      }
    `;
  }};
`;

const DragAndDropIcons = styled.div`
  position: relative;
  padding: ${({ theme }) => theme.spacing(2)};
`;

const UploadIcon = styled.img`
  z-index: 2;
  position: relative;
  margin-top: 30px;
  margin-left: 25px;
`;

const DocIcon = styled.img`
  position: absolute;
  left: 0;
  right: 0;
  margin: auto;
  transform: translateX(-5px);
  z-index: 1;
`;

const BrowseButton = styled(Button)`
  color: ${({ theme }) => theme.palette.getContrastText(theme.palette.background.paper)};
  opacity: 1;
  font-size: inherit;
  padding: 0;
  text-decoration: underline;
  margin-left: ${({ theme }) => theme.spacing(0.5)};
`;

const IconWrapper = styled.div`
  opacity: ${({ theme }) => (theme.palette.mode === 'dark' ? 1 : 0.5)};
`;
